import * as React from 'react';

import {
  Col,
  ConsoleDialog,
  Grid,
  Row,
  TouchButton,
  TouchSelect,
} from '@rematter/ui-web';
import { LucideMaximize2 } from 'lucide-react';

import { ScaleCameraStream } from '../../_shared/ItemsInterface/widgets/ScaleCameraStream';

import { useScales } from '~/modules/trade/hooks/useScales';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type CameraPlaceholderProps = { label: string };

//
// ⚛️ ⚛️ ⚛️ CAMERA PLACEHOLDER CARD ⚛️ ⚛️ ⚛️
//
const CameraPlaceholderCard = ({ label }: CameraPlaceholderProps) => {
  const t = useTranslations();

  return (
    <div className="@CameraPlaceholderCard relative flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-neutral/10">
      <div className="flex flex-col items-center gap-1 text-neutral/40">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral/20 border-t-neutral/50" />
        <span className="text-xs">
          {t.scaleTicketPro.cameraModePlaceholder}
        </span>
      </div>
      <span className="absolute right-1 bottom-1 rounded bg-neutral/60 px-1.5 py-0.5 text-[10px] text-white">
        {label}
      </span>
    </div>
  );
};

type CameraCardProps = {
  scaleName: string;
  cameraId: string;
  sidecarId: string;
  streamProfile: string | null;
  cameraSidecar: {
    id: string;
    name: string;
    peerId: string | null;
    multiaddrs: string[];
    status: string;
  } | null;
};

//
// ⚛️ ⚛️ ⚛️ CAMERA CARD ⚛️ ⚛️ ⚛️
//
const CameraCard = ({
  scaleName,
  cameraId,
  sidecarId,
  streamProfile,
  cameraSidecar,
}: CameraCardProps) => (
  <div className="@CameraCard relative w-full overflow-hidden rounded-lg">
    <ScaleCameraStream
      cameraId={cameraId}
      sidecarId={sidecarId}
      streamProfile={streamProfile}
      cameraSidecar={cameraSidecar}
    />
    <span className="absolute right-1 bottom-1 rounded bg-neutral/60 px-1.5 py-0.5 text-[10px] text-white">
      {scaleName}
    </span>
  </div>
);

//
// ⚛️ ⚛️ ⚛️ CAMERA FEED SIDEBAR ⚛️ ⚛️ ⚛️
//
export const CameraFeedSidebar = () => {
  const t = useTranslations();

  const { allScales, setCurrentScaleId } = useScales();

  const [isExpanded, setIsExpanded] = React.useState(false);

  const scalesWithCameras = React.useMemo(
    () => allScales.filter((s) => s.camera !== null),
    [allScales],
  );

  const selectOptions = React.useMemo(
    () => [
      { key: 'all', label: t.scaleTicketPro.allSelected },
      ...scalesWithCameras.map((s) => ({ key: s.id, label: s.name })),
    ],
    [scalesWithCameras, t.scaleTicketPro.allSelected],
  );

  const [selectedCamera, setSelectedCamera] = React.useState('all');

  React.useEffect(() => {
    if (
      selectedCamera !== 'all' &&
      !scalesWithCameras.some((s) => s.id === selectedCamera)
    ) {
      setSelectedCamera('all');
    }
  }, [scalesWithCameras, selectedCamera]);

  const visibleCameras = React.useMemo(() => {
    if (selectedCamera === 'all') return scalesWithCameras;
    return scalesWithCameras.filter((s) => s.id === selectedCamera);
  }, [scalesWithCameras, selectedCamera]);

  const handleSelectChange = React.useCallback(
    (value: string) => {
      setSelectedCamera(value);
      if (value !== 'all') {
        setCurrentScaleId(value);
      } else {
        setCurrentScaleId(null);
      }
    },
    [setCurrentScaleId],
  );

  const cameraCards =
    visibleCameras.length > 0 ? (
      visibleCameras.map((scale) => {
        const camera = scale.camera;
        if (!camera) return null;
        return (
          <CameraCard
            key={scale.id}
            scaleName={scale.name}
            cameraId={camera.id}
            sidecarId={camera.sidecarId}
            streamProfile={camera.streamProfile}
            cameraSidecar={camera.sidecar}
          />
        );
      })
    ) : scalesWithCameras.length === 0 ? (
      <CameraPlaceholderCard label={t.scaleTicketPro.noCamerasAvailable} />
    ) : null;

  return (
    <>
      <div className="@CameraFeedSidebar flex w-72 shrink-0 flex-col border-l border-neutral/10">
        <Row gap2 yCenter xBetween className="border-b border-neutral/10 p-2">
          <TouchSelect
            className="flex-1"
            value={selectedCamera}
            options={selectOptions}
            onChange={handleSelectChange}
          />
          <TouchButton
            variant="outline"
            aria-label={t.scaleTicketPro.expandCameraView}
            onPress={() => setIsExpanded(true)}
            isDisabled={scalesWithCameras.length === 0}
          >
            <LucideMaximize2 size={16} />
          </TouchButton>
        </Row>
        <div className="no-scrollbar flex-1 overflow-y-auto p-2">
          <Col gap2>{!isExpanded && cameraCards}</Col>
        </div>
      </div>

      <ConsoleDialog
        isOpen={isExpanded}
        onOpenChange={setIsExpanded}
        size="fill"
        title={
          <Row gap2 yCenter>
            {t.scaleTicketPro.expandCameraView}
          </Row>
        }
        content={
          <Grid
            gap4
            className="grid-cols-[repeat(auto-fill,minmax(400px,1fr))]"
          >
            {cameraCards}
          </Grid>
        }
      />
    </>
  );
};
